import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, BarChart3, Wallet, MessageSquare, FileText, User, Package, LogOut, ChevronRight } from 'lucide-react';
import BrandWordmark from './common/BrandWordmark';

export const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();

  // Get company profile from localStorage
  const companyProfile = JSON.parse(localStorage.getItem('companyProfile') || '{}');
  const isProduct = companyProfile?.business_type === 'product';
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : {};

  const menuItems = [
    { icon: BarChart3, label: 'Dashboard', path: '/dashboard' },
    { icon: Wallet, label: 'Transactions', path: '/transactions' },
    { icon: FileText, label: 'Categories', path: '/categories' },
    { icon: BarChart3, label: 'Analytics & Reports', path: '/analytics-reports' },
    ...(isProduct ? [{ icon: Package, label: 'Stocks', path: '/stocks' }] : []),
    { icon: MessageSquare, label: 'Chat Interface', path: '/chat-interface' },
  ];

  const bottomItems = [
    { icon: User, label: 'Profile', path: '/profile' },
    // { icon: Settings, label: 'Settings', path: '/settings' }, // TODO: Implement settings later
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('companyProfile');
    localStorage.removeItem('company_id');
    window.location.href = '/login';
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm md:hidden z-30"
          onClick={onClose}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`${
          isOpen 
            ? 'translate-x-0' 
            : '-translate-x-full'
        } md:translate-x-0 ${
          isOpen 
            ? 'w-64' 
            : 'md:w-20 w-0 overflow-hidden'
        } fixed md:relative left-0 top-0 h-screen transform transition-all duration-300 z-40 md:z-30 flex flex-col bg-white border-r border-gray-200 dark:bg-slate-800 dark:border-none`}
      >
        {/* Logo & Toggle */}
        <div className={`flex items-center transition-all duration-300 ${
          isOpen 
            ? 'justify-between p-5' 
            : 'justify-center p-4 md:p-3'
        }`}>
          <div className={`flex items-center gap-3 transition-all duration-300 ${isOpen ? 'opacity-100' : 'hidden'}`}>
            <div>
              <BrandWordmark className="h-14 w-14" />
              <p className="text-[10px] text-gray-400 dark:text-slate-400 -mt-0.5">Financial Management</p>
            </div>
          </div>
          {/* Collapsed logo */}
          <div className={`${isOpen ? 'hidden' : 'hidden md:flex'} items-center justify-center`}>
            <div className="w-11 h-11 flex items-center justify-center">
              <img src="/new-logo.svg" alt="Accurix" className="w-11 h-11 object-contain" />
            </div>
          </div>
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="p-2.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors focus:outline-none cursor-pointer"
            aria-label="Toggle sidebar"
            type="button"
          >
            <Menu className="w-5 h-5 text-gray-500 dark:text-slate-400" />
          </button>
        </div>

        {/* Divider */}
        <div className="mx-4 border-t border-gray-200 dark:border-slate-700"></div>

        {/* Main Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
          {isOpen && (
            <p className="px-3 mb-2 text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500">Menu</p>
          )}
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center rounded-lg transition-all duration-200 whitespace-nowrap relative ${
                  active
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-600/20 dark:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-700/60 dark:hover:text-slate-200'
                } ${
                  isOpen 
                    ? 'gap-3 px-4 py-3' 
                    : 'md:gap-0 md:p-3 md:justify-center gap-3 px-4 py-3'
                }`}
                onClick={onClose}
                title={!isOpen ? item.label : ''}
              >
                {active && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-blue-500 rounded-r-full"></div>
                )}
                <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-blue-600 dark:text-blue-400' : ''}`} />
                <span className={`text-sm font-medium ${isOpen ? 'block' : 'hidden md:hidden'}`}>{item.label}</span>
                {isOpen && active && (
                  <ChevronRight className="w-4 h-4 ml-auto text-gray-400 dark:text-slate-500" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Divider */}
        <div className="mx-4 border-t border-gray-200 dark:border-slate-700"></div>

        {/* Bottom Section */}
        <div className="px-3 py-3 space-y-1">
          {isOpen && (
            <p className="px-3 mb-2 text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500">Account</p>
          )}
          {bottomItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center rounded-lg transition-all duration-200 whitespace-nowrap ${
                  active
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-600/20 dark:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-700/60 dark:hover:text-slate-200'
                } ${
                  isOpen 
                    ? 'gap-3 px-4 py-2.5' 
                    : 'md:gap-0 md:p-3 md:justify-center gap-3 px-4 py-2.5'
                }`}
                onClick={onClose}
                title={!isOpen ? item.label : ''}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-blue-600 dark:text-blue-400' : ''}`} />
                <span className={`text-sm font-medium ${isOpen ? 'block' : 'hidden md:hidden'}`}>{item.label}</span>
              </Link>
            );
          })}

          {/* Logout */}
          <button
            onClick={handleLogout}
            className={`w-full group flex items-center rounded-lg transition-all duration-200 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:text-slate-400 dark:hover:bg-red-500/10 dark:hover:text-red-400 ${
              isOpen 
                ? 'gap-3 px-4 py-2.5' 
                : 'md:gap-0 md:p-3 md:justify-center gap-3 px-4 py-2.5'
            }`}
            title={!isOpen ? 'Logout' : ''}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span className={`text-sm font-medium ${isOpen ? 'block' : 'hidden md:hidden'}`}>Logout</span>
          </button>
        </div>

        {/* User Card */}
        {isOpen && (
          <div className="mx-3 mb-4 p-3 rounded-lg bg-gray-100 dark:bg-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-600/30 flex items-center justify-center text-blue-600 dark:text-blue-300 font-semibold text-sm">
                {(user?.email || 'U')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-slate-200 truncate">{user?.email || 'User'}</p>
                <p className="text-[10px] text-gray-400 dark:text-slate-500">{companyProfile?.name || 'Company'}</p>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};
